import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiFolder,
  FiUserPlus,
  FiUsers,
  FiX,
  FiMoreVertical,
  FiEye,
  FiEyeOff,
  FiPlusCircle,
  FiTrash2,
} from "react-icons/fi"; // Folder icon
import { API_BASE_URL } from "../utils/crudFunctions";
import { getToken, getUserRole } from "../utils/auth";
import UserSidebar from "../components/UserSidebar";
const AllProjects = () => {
  const { userName } = useParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [showUserSidebar, setShowUserSidebar] = useState(false);
  const [token, setToken] = useState("");
  const [users, setUsers] = useState([]);
  const [showAssignMenu, setShowAssignMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("date-desc"); // default sort by newest first
  const navigate = useNavigate();

  const role = getUserRole();
  useEffect(() => {
    const token = getToken();

    if (!token) {
      navigate("/login");
    }
    setToken(token);
  }, [navigate]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/projects/${userName}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(response.data.projects);
      console.log("projects", response.data.projects);
    } catch (err) {
      console.error(err);
      setError("Failed to load projects.");
    } finally {
      setLoading(false);
    }
  }, [token, userName]); // ✅ stable deps

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get_users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  }, [token]); // ✅ stable deps

  useEffect(() => {
    if (token) {
      fetchProjects();
      fetchUsers();
    }
  }, [token, fetchProjects, fetchUsers]);

  const filteredAndSortedProjects = projects
    .filter(project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortOption) {
        case "date-asc":
          return new Date(a.created_at) - new Date(b.created_at);
        case "date-desc":
          return new Date(b.created_at) - new Date(a.created_at);
        case "creator-asc":
          return a.created_by.localeCompare(b.created_by);
        case "creator-desc":
          return b.created_by.localeCompare(a.created_by);
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });



  const handleDelete = async (projectName) => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${projectName}"? This action cannot be undone.`
    );

    if (!isConfirmed) {
      return { success: false, message: "Deletion cancelled" };
    }

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete_project/${encodeURIComponent(projectName)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setProjects((prevProjects) =>
        prevProjects.filter((proj) => proj.name !== projectName)
      );
      // Handle successful deletion
      // console.log("Project deleted:", response.data);
      alert("Project deleted successfully");

      return {
        success: true,
        message: response.data.message,
        deletedProject: response.data.deleted_project,
      };
    } catch (error) {
      console.error(
        "Error deleting project:",
        error.response?.data || error.message
      );

      // Revert optimistic update if the deletion failed
      setProjects(projects); // Reset to original projects array

      // Handle different error scenarios
      let errorMessage = "Failed to delete project";
      if (error.response) {
        errorMessage = error.response.data.error || errorMessage;
      } else if (error.request) {
        errorMessage = "No response from server";
      } else {
        errorMessage = error.message;
      }

      alert(`Error: ${errorMessage}`);
      return {
        success: false,
        error: errorMessage,
        status: error.response?.status,
      };
    }
  };

  return (
    <div className="mt-14 px-6 py-10 bg-gray-50 min-h-screen">
      {/* Header + Buttons */}
      {/* Header + Buttons */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Projects for <span className="text-indigo-600">{userName}</span>
        </h1>
        <div className="flex gap-2">
          {role === "admin" && (
            <>
              <button
                onClick={() => setIsUserModalOpen(true)}
                className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg shadow text-sm"
              >
                <FiUserPlus /> Add User
              </button>
              <button
                onClick={() => setShowUserSidebar(!showUserSidebar)}
                className="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg shadow text-sm"
              >
                <FiUsers /> Manage Users
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search projects..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute left-3 top-2.5 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="creator-asc">Creator (A-Z)</option>
            <option value="creator-desc">Creator (Z-A)</option>
          </select>
        </div>
      </div>

      {isUserModalOpen && (
        <AddUserModal
          onClose={() => setIsUserModalOpen(false)}
          onUserAdded={() => {
            alert("User Added Successfully.");
          }}
          token={token}
        />
      )}

      {showUserSidebar && (
        <UserSidebar onClose={() => setShowUserSidebar(false)} users={users} />
      )}

      {loading && <p className="text-gray-500">Loading projects...</p>}
      {error && <p className="text-red-500">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {
          filteredAndSortedProjects.length === 0 ? (
            <div className="col-span-full text-center py-10">
              {projects.length === 0 ? (
                <p className="text-gray-500">No projects found</p>
              ) : (
                <p className="text-gray-500">No projects match your search</p>
              )}
            </div>
          ) : (
            filteredAndSortedProjects.map((project, index) => (
              <div
                key={index}
                className="relative bg-white cursor-pointer rounded-2xl shadow-md hover:shadow-lg transition-all p-4 border border-gray-200"
              >
                <div className="flex justify-between items-center mb-3">
                  <div
                    onClick={() =>
                      navigate(`/project/${userName}/${project.name}?from=backend`)
                    }
                    className="flex items-center gap-3"
                  >
                    <FiFolder className="text-indigo-500" size={24} />
                    <div className="flex flex-col items-start">
                      <h2 className="text-lg font-semibold text-gray-800 cursor-pointer hover:text-indigo-600 transition-colors">
                        {project.name}
                      </h2>
                      <p className="text-xs text-gray-500">
                        Created At -{" "}
                        {project.created_at
                          ? new Date(project.created_at).toLocaleDateString("en-GB")
                          : "N/A"}
                      </p>
                      <p className="text-xs text-gray-500">
                        Created By -{" "}
                        {project.created_by
                          ? project.created_by
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Delete Button - Only show for admin */}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(project.name);
                      }}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                      title="Delete project"
                    >
                      <FiTrash2 size={18} />
                    </button>

                    {/* Existing More Options Button */}
                    {(role === "admin" || role === "manager") && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAssignMenu(
                            showAssignMenu === index ? null : index
                          );
                        }}
                        className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                        title="More options"
                      >
                        <FiMoreVertical size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {/* image */}
                {project.imageBase64 ? (
                  <img
                    src={project.imageBase64}
                    alt={project.name}
                    className="w-full h-40 object-cover rounded-lg"
                    onClick={() =>
                      navigate(`/project/${userName}/${project.name}?from=backend`)
                    }
                  />
                ) : (
                  <div className="w-full h-40 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">
                    No Image
                  </div>
                )}
              </div>
            )))}
        {
          <div className="bg-white cursor-pointer rounded-2xl shadow-md hover:shadow-lg transition-all p-4 border border-gray-200 flex flex-col items-center justify-center text-center gap-4 h-64">
            <FiPlusCircle className="text-indigo-500" size={48} />
            <button
              onClick={() => {
                navigate("/add-project");
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
            >
              Add New Project
            </button>
          </div>
        }
      </div>
    </div>
  );
};

export default AllProjects;

const AddUserModal = ({ onClose, onUserAdded, token }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user"); // Default role
  const [validUptoDays, setValidUptoDays] = useState(365); // Default validity
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleAddUser = async () => {
    if (!token) {
      console.error("No Token");
      navigate("/login");
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/add_user`,
        {
          username,
          password,
          role,
          valid_upto_days: validUptoDays
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUserAdded();
      onClose();
    } catch (err) {
      console.error("Failed to add user", err);
      alert(err.response?.data?.error || "Failed to add user");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Add New User</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              placeholder="Enter username"
              className="w-full border px-3 py-2 rounded"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              className="w-full border px-3 py-2 rounded"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 transform -translate-y-1/2 text-gray-500"
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Validity (days)
            </label>
            <input
              type="number"
              min="1"
              className="w-full border px-3 py-2 rounded"
              value={validUptoDays}
              onChange={(e) => setValidUptoDays(parseInt(e.target.value) || 1)}
            />
          </div>

          <button
            onClick={handleAddUser}
            className="bg-indigo-600 text-white px-4 py-2 rounded w-full hover:bg-indigo-700 mt-4"
          >
            Add User
          </button>
        </div>
      </div>
    </div>
  );
};

// const UserAssignmentInput = ({ user, project, onAssign, token }) => {
//   const [inputValue, setInputValue] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [userStats, setUserStats] = useState(null);
//   const maxAvailable = project.totalImages - (project.assignedImages || 0);

//   // Fetch user stats when component mounts or project/user changes
//   useEffect(() => {
//     const fetchUserStats = async () => {
//       try {
//         setLoading(true);
//         const response = await axios.get(
//           `${API_BASE_URL}/admin/projects/${project.name}/labeling_stats/${user.username}`,
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );
//         setUserStats(response.data.stats);
//       } catch (err) {
//         console.error(err);
//         setError(err.response.data.error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchUserStats();
//   }, [project.name, user.username]);

//   const handleAssign = () => {
//     const count = parseInt(inputValue);

//     if (isNaN(count) || count <= 0) {
//       alert("Please enter a valid positive number");
//       return;
//     }

//     if (count > maxAvailable) {
//       alert(`Cannot assign more than ${maxAvailable} images`);
//       return;
//     }

//     onAssign(user.username, count);
//     setInputValue("");
//   };

//   return (
//     <div className="mb-3 last:mb-0">
//       <div className="flex items-center justify-between mb-1">
//         <span className="text-sm font-medium text-gray-700">
//           {user.username}
//         </span>
//         {userStats && (
//           <span className="text-xs text-gray-500">
//             Assigned: {userStats.totalAssigned || 0} • Labeled:{" "}
//             {userStats.labeled || 0}
//           </span>
//         )}
//         {loading && <span className="text-xs text-gray-500">Loading...</span>}
//         {error && <span className="text-xs text-red-500">{error}</span>}
//       </div>
//       <div className="flex gap-2">
//         <input
//           type="text"
//           inputMode="numeric"
//           pattern="[0-9]*"
//           value={inputValue}
//           onChange={(e) => {
//             const value = e.target.value.replace(/\D/g, "");
//             setInputValue(value);
//           }}
//           placeholder="Enter count"
//           className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
//         />
//         <button
//           onClick={handleAssign}
//           disabled={
//             !inputValue ||
//             parseInt(inputValue) <= 0 ||
//             parseInt(inputValue) > maxAvailable
//           }
//           className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
//         >
//           Assign
//         </button>
//       </div>
//     </div>
//   );
// };
